import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import { space, layout, SpaceProps, LayoutProps } from 'styled-system';
import { Heading } from '../SubComponents/Text';

const StepHeading = styled(Heading).attrs((props) => ({
  // Prevent border flashing
  style: {
    borderImageSource: `url("${props.theme.stepBorder}")`,
  },
}))<{ currentStep: boolean }>`
  border-width: 0 0 6px 0;
  border-style: dotted;
  border-color: ${(props) => props.theme.textColor};
  border-image-slice: 33% 30%;
  border-image-repeat: repeat;
  opacity: ${(props) => (props.currentStep ? '1' : '0.4')};
  cursor: pointer;
  ${space}
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 5px;
  /* @media screen and (max-width: 767px) {
    align-items: center;
  } */
`;

interface CustomStepProps {
  children: React.ReactNode;
  heading: string;
  currentStep: boolean;
  className?: string;
  onClick?: () => void;
}

type StepProps = CustomStepProps & SpaceProps & LayoutProps;

let Step = ({ children, heading, currentStep, className, onClick }: StepProps) => {
  const step = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    currentStep ? openStep() : closeStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Disgusting JS animations because CSS is dumb! https://css-tricks.com/using-css-transitions-auto-dimensions
  // TODO: Fix this! There must be a better way!

  const openStep = () => {
    if (!step.current?.hasAttribute('data-is-open')) {
      // First time run, this prevents animations on reload
      step.current?.setAttribute('data-is-open', 'true');
      step.current?.setAttribute('data-is-animating', 'false');
    } else {
      step.current.setAttribute('data-is-animating', 'true');
      const elementHeight = step.current?.scrollHeight;
      step.current.style.height = elementHeight + 'px';
      step.current.style.overflow = 'hidden';

      const markAsOpen = () => {
        if (step.current && step.current?.style.height !== '2.6rem') {
          // Event listener trigers for both open and closed steps, so this check is needed
          step.current.style.height = '';
          step.current.style.overflow = '';
          step.current?.setAttribute('data-is-open', 'true');
          setTimeout(() => step.current?.setAttribute('data-is-animating', 'false'), 500);
        }
        step.current?.removeEventListener('transitionend', markAsOpen);
      };

      step.current?.addEventListener('transitionend', markAsOpen);
    }
  };

  const closeStep = () => {
    if (!step.current?.hasAttribute('data-is-open')) {
      // First time run, this prevents animations on reload
      step.current?.setAttribute('data-is-open', 'false');
      step.current?.setAttribute('data-is-animating', 'false');
    } else {
      step.current.setAttribute('data-is-animating', 'true');
      const elementHeight = step.current?.scrollHeight;
      step.current.style.height = elementHeight + 'px';

      // wait until next frame to set closed height else the step closes without animation
      requestAnimationFrame(() => {
        if (!step.current) return;

        step.current.style.height = '2.6rem';
        step.current.style.overflow = 'hidden';

        const markAsClosed = () => {
          if (step.current?.style.height === '2.6rem') {
            // Event listener trigers for both open and closed steps, so this check is needed
            step.current.style.height = '';
            step.current.style.overflow = '';
            step.current.setAttribute('data-is-open', 'false');
            setTimeout(
              () => step.current?.setAttribute('data-is-animating', 'false'),
              500
            );
          }
          step.current?.removeEventListener('transitionend', markAsClosed);
        };

        step.current?.addEventListener('transitionend', markAsClosed);
      });
    }
  };

  return (
    <div className={className} ref={step}>
      <StepHeading onClick={onClick} currentStep={currentStep} mb="4" textAlign="center">
        {heading}
      </StepHeading>
      <StepContent>{children}</StepContent>
    </div>
  );
};

Step = styled(Step)`
  transition: all 0.6s;
  margin-bottom: 32px;
  ${space}
  ${layout}

  /* Styles for closed steps */
  &[data-is-open='false'] {
    height: ${(props) => (props.theme.name === 'starboard' ? '3.6rem' : '2.6rem')};
    overflow: hidden;

    ${StepContent} {
      display: none;
    }
  }

  &[data-is-animating='true'] {
    ${StepContent} {
      display: flex;
    }
  }
  &:last-child {
    margin-bottom: 64px;
  }
`;

export default Step;
